import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from 'mantine-react-table'
import { Text } from '@mantine/core'
import { useSessionHistory } from '../api/session-history-api'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { UserSessionLogListItem } from '@/shared/api/topli/TopliApi'
import {
  formatDateForSending,
  formatDateForTable,
  formatDurationFromDates,
} from '@/shared/lib/utils'
import { useDebounce } from '@/shared/lib/useDebounce'
import { useTableFilters } from '@/shared/hooks/useTableFilters'

export function useSessionHistoryTable(user_id?: number) {
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const { getDateRange } = useTableFilters(columnFilters)
  const debouncedSearch = useDebounce(search, 500)
  const { t } = useTranslation(['userManagement'])
  const { language } = useLanguageStore()

  const memoizedSorting = useMemo(() => {
    let orderBy = ''
    if (!sorting.length) return null
    sorting.forEach(item => (item.desc ? (orderBy = `-${item.id}`) : (orderBy = `${item.id}`)))
    return orderBy
  }, [sorting])

  const dateRange = getDateRange('user__last_login')

  const {
    data: sessionHistory,
    isLoading: isLoadingSessionHistory,
    isError: isErrorSessionHistory,
    isFetching: isFetchingSessionHistory,
  } = useSessionHistory({
    sort: memoizedSorting,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    search: debouncedSearch,
    ...(user_id ? { user_id } : {}),
    ...(dateRange ? { dateRange } : {}),
  })

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [debouncedSearch, columnFilters])

  const handleSearchChange = (value: string) => {
    setSearch(value)
  }

  const columns = useMemo<MRT_ColumnDef<UserSessionLogListItem>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 100,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'user__fullname',
        enableColumnFilter: false,
        header: t('userManagement:username'),
        Cell: ({ row }) => (
          <div className='text-sm font-semibold'>{row.original.user.fullname}</div>
        ),
      },
      {
        header: t('userManagement:session_history_table.date'),
        accessorKey: 'user__last_login',
        filterVariant: 'date-range',
        Cell: ({ row }) =>
          row.original.user.last_login
            ? formatDateForTable(row.original.user.last_login, language)
            : '-',
      },
      {
        header: t('userManagement:session_history_table.session_duration'),
        accessorKey: 'session_duration',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          row.original.start_session && row.original.end_session ? (
            <Text
              lineClamp={4}
              classNames={{
                root: 'flex flex-col w-full mb-6 text-sm font-body',
              }}
            >
              {formatDurationFromDates(
                row.original.start_session,
                row.original.end_session,
                language,
                t,
              )}
            </Text>
          ) : (
            '-'
          ),
      },
      {
        header: t('userManagement:session_history_table.browser'),
        accessorKey: 'browser',
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.browser}
          </Text>
        ),
      },
      {
        header: t('userManagement:session_history_table.device_type'),
        accessorKey: 'device',
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.device}
          </Text>
        ),
      },
    ],
    [language],
  )

  return {
    fetchedSessionHistory: sessionHistory?.data ?? [],
    totalRowCount: sessionHistory?.total ?? 0,
    columns,
    pagination,
    isErrorSessionHistory,
    isFetchingSessionHistory,
    isLoadingSessionHistory,
    sorting,
    setPagination,
    setSorting,
    columnFilters,
    setColumnFilters,
    search,
    handleSearchChange,
  }
}
